/**
 * RestoID — MFA phone (SMS) verification code.
 * ---------------------------------------------------------------------------
 * Twin of `MfaEmailVerification`, and for the same reasons: a RestoID frame around
 * the PRISTINE upstream MFA code logic, restated once in
 * `custom/components/OtpEntry.tsx` as `MfaCodeEntry` (hooks, guards, effects and
 * error branches unchanged — only the markup is ours). Both upstream guards are
 * kept verbatim, so an invalid session and a missing verification ID still land on
 * the same ErrorPage with the same `rawMessage`.
 * Swapped in for `./pages/MfaVerification/PhoneVerificationCode`.
 *
 * Deliberate differences from upstream:
 *   - one ScreenHead instead of SecondaryPageLayout + SectionLayout printing the
 *     screen name twice; the layout title stays the document title in `pageMeta`,
 *     where upstream had it.
 *   - `mfa.enter_phone_verification_code_description` is replaced by the design's
 *     "code sent to …" chip, which carries the masked number — the only part of that
 *     sentence the user needs. No chip when the server sent no masked identifier.
 *   - The masked number is printed exactly as the server masked it: unlike the
 *     sign-in OTP screen there is no local number to re-format, and re-grouping a
 *     string that already contains bullets would corrupt it.
 *   - NavBar back arrow → ScreenHead back pill (same `navigate(-1)`);
 *     `SwitchMfaFactorsLink` → the shared RestoID `MfaSwitchLink`, with the same
 *     "fewer than two factors → render nothing" rule.
 */
import { MfaFactor, SignInIdentifier } from '@logto/schemas';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';

import FirstScreenLayout from '@/Layout/FirstScreenLayout';
import UserInteractionContext from '@/Providers/UserInteractionContextProvider/UserInteractionContext';
import useMfaFlowState from '@/hooks/use-mfa-factors-state';
import useNavigateWithPreservedSearchParams from '@/hooks/use-navigate-with-preserved-search-params';
import { UserMfaFlow } from '@/types';
import { codeVerificationTypeMap } from '@/utils/sign-in-experience';

import { MfaSwitchLink } from '../components/MfaFactorRows';
import { MfaCodeEntry, SentToChip } from '../components/OtpEntry';
import { ScreenHead, Stack } from '../components/restoid';
import { useCopy } from '../copy';

import ErrorPage from './ErrorPage';

const PhoneVerificationCode = () => {
  const { t } = useTranslation();
  const c = useCopy();
  const navigate = useNavigateWithPreservedSearchParams();
  const flowState = useMfaFlowState();
  const { verificationIdsMap } = useContext(UserInteractionContext);

  if (!flowState) {
    return <ErrorPage title="error.invalid_session" />;
  }

  // VerificationId not found
  const verificationId = verificationIdsMap[codeVerificationTypeMap[SignInIdentifier.Phone]];
  if (!verificationId) {
    return <ErrorPage title="error.invalid_session" rawMessage="Verification ID not found" />;
  }

  const maskedPhone = flowState.maskedIdentifiers?.[MfaFactor.PhoneVerificationCode];

  return (
    <FirstScreenLayout pageMeta={{ titleKey: 'mfa.verify_mfa_factors' }}>
      <Stack gap={22}>
        <ScreenHead
          back={() => navigate(-1)}
          backLabel={c('backShort')}
          title={t('mfa.enter_phone_verification_code')}
        />
        {maskedPhone ? <SentToChip target={maskedPhone} /> : null}
        <MfaCodeEntry identifierType={SignInIdentifier.Phone} verificationId={verificationId} />
        <MfaSwitchLink flow={UserMfaFlow.MfaVerification} flowState={flowState} />
      </Stack>
    </FirstScreenLayout>
  );
};

export default PhoneVerificationCode;
